import Icon from 'ui/Icon';
import { Box, Flex } from 'ui/Layout';
import Tooltip from 'ui/Tooltip';
import { Text } from 'ui/Typography';

import { flickerAnimation, spinAnimation } from '../../../constants';
import { useSankeyAutosaveContext } from '../../../contexts/sankeyAutosaveContext';
import { useCurrentUser } from '../../../utils/user';
import SaveButton from './SaveButton';
import { useTimeAgo } from './utils';

// TODO: Move this to a shared location to be used by the <Icon /> component.
const savedInCloudIcon = (
  <svg
    width="20px"
    height="20px"
    viewBox="0 0 20 20"
    fill="currentColor"
    stroke="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M14.8 9c.1-.3.2-.6.2-1 0-2.2-1.8-4-4-4-1.5 0-2.9.9-3.5 2.2-.3-.1-.7-.2-1-.2C5.1 6 4 7.1 4 8.5c0 .2 0 .4.1.5-1.8.3-3.1 1.7-3.1 3.5C1 14.4 2.6 16 4.5 16h10c1.9 0 3.5-1.6 3.5-3.5 0-1.8-1.4-3.3-3.2-3.5zm-6.3 5.9l-3.2-3.2 1.4-1.4 1.8 1.8 3.8-3.8 1.4 1.4-5.2 5.2z" />
  </svg>
);

const SaveInfo: React.FC = () => {
  const { user } = useCurrentUser();
  const { isSaving, lastSaveTimestamp, lastSaveError } =
    useSankeyAutosaveContext();

  const timeAgo = useTimeAgo(lastSaveTimestamp);

  if (!user?.can_update_sankeys) {
    return <SaveButton />;
  }

  if (isSaving) {
    return (
      <Icon
        animation={`${spinAnimation} 1s infinite linear`}
        color="gray.500"
        fontSize="sm"
        icon="arrows-rotate"
      />
    );
  }

  if (lastSaveError) {
    return (
      <Tooltip showArrow content={<Text>{lastSaveError}</Text>}>
        <Flex
          alignItems="center"
          animation={`${flickerAnimation} 1s infinite`}
          color="red.600"
          cursor="help"
          gap={1}
          pl={2}
        >
          <Icon fontSize="lg" icon="triangle-exclamation" />
          <Text fontSize="sm" fontWeight={600} whiteSpace="nowrap">
            Save error
          </Text>
        </Flex>
      </Tooltip>
    );
  }

  if (lastSaveTimestamp) {
    return (
      <Tooltip
        showArrow
        content={
          <Text lineHeight={2}>
            Your diagram has been saved to the cloud.
            <br />
            Last edit: <b>{timeAgo}</b>
          </Text>
        }
      >
        <Box color="gray.400" cursor="help">
          {savedInCloudIcon}
        </Box>
      </Tooltip>
    );
  }

  return null;
};

export default SaveInfo;
